import React from 'react'
import { Outlet } from 'react-router-dom'

import { useQueryParamsSyncWithAvailability } from '@hooks/useQueryParamsSyncWithAvailability'
import { useSiteContext } from '@contexts/MkSiteContext'
import { MkAuthLoadingScreen } from '@account/components/SignIn'
import { useAuthentication } from '@auth/hooks/useAuthentication'
import { MkThemeSwitcher } from '@ui/ThemeSwitcher/MkThemeSwitcher'
import { SCROLL_THRESHOLD } from '@constants'
import { useLDFlagsWithDevOverrides } from '@hooks/useLaunchDarkly'
import { createLogger } from '@utils/Logger'
import { markPerformanceStart, markPerformanceEnd } from '@utils/perfUtils'
import { SiteNav } from './SiteNav'
import { SiteFooter } from './SiteFooter'

const log = createLogger('SiteParent')

export interface SiteParentProps {
  themeSwitcher?: boolean
  promoHeader?: boolean
  [key: string]: any
}

export const SiteParent: React.FC<SiteParentProps> = ({ themeSwitcher, promoHeader }) => {
  const { themeBrand } = useSiteContext()
  const themeToken = (themeBrand || '').toLowerCase() === 'ev' ? 'ebac' : themeBrand
  const [scrolled, setScrolled] = React.useState(false)

  const { isAuthenticated, isAuthorized, accessToken, idToken } = useAuthentication()
  const { switchMeasurePerf } = useLDFlagsWithDevOverrides<any>()

  const isLoading = React.useMemo(() => isAuthenticated === true && isAuthorized === false && !accessToken && !idToken, [isAuthenticated, isAuthorized, accessToken])

  // Track loader display time for performance monitoring
  React.useEffect(() => {
    if (switchMeasurePerf) {
      if (isLoading) {
        markPerformanceStart('login-loader')
      } else {
        markPerformanceEnd('login-loader', log)
      }
    }
  }, [isLoading, switchMeasurePerf])

  // Handle scroll detection for promo header
  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > SCROLL_THRESHOLD)
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // Sync URL query parameters with availability context
  useQueryParamsSyncWithAvailability()

  return (
    <div
      id="theme-root"
      data-theme={themeToken}
      className={promoHeader ? `promo-header ${scrolled ? 'scrolled' : ''}` : undefined}>
      {/* Skip navigation link for keyboard users */}
      <a
        href="#mainView"
        className="skip-link"
        tabIndex={0}>
        Skip to main content
      </a>

      {themeSwitcher && <MkThemeSwitcher />}

      <SiteNav
        brand={themeBrand}
        promoHeader={promoHeader}
        scrolled={scrolled}
      />

      <main
        id="mainView"
        className="main-view">
        <Outlet />
        <MkAuthLoadingScreen show={isLoading} />
      </main>

      <SiteFooter brand={themeBrand} />
    </div>
  )
}
